/**
 * Investor layout — escape-hatch case for CLAUDE.md's responsive-UI rule.
 *
 * Mobile and desktop are not different *layouts* of the same feature; they
 * are different *interaction models*:
 *
 *   - Desktop: persistent left sidebar (always-visible navigation)
 *   - Mobile: top bar with Burger that opens a slide-in Drawer overlay
 *             with focus trap + auto-close on route change
 *
 * No `@container` rule can express "render this as a modal overlay below
 * 768px and an inline sibling above 768px" — they are structurally
 * different React subtrees with different state machines.
 *
 * This is the ONLY file in the investor app where the
 * `useIsMobile` / `if (isMobile) return ...` pattern is permitted. All
 * other surfaces use container queries.
 */
import Logo from "@funded-here-interface/common/src/assets/logo.png";
import ContentLoader from "@funded-here-interface/common/src/components/ContentLoader/ContentLoader";
import { useIsMobile } from "@funded-here-interface/common/src/hooks/useIsMobile";
import { hasFullInvestorAccess } from "@funded-here-interface/common/src/Utils/investorAccess";
import { ActionIcon, Burger, Drawer } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconChartBarPopular,
  IconFileDollar,
  IconHistory,
  IconFileDescription,
  IconShieldCheck,
  IconX,
} from "@tabler/icons-react";
import { FC, ReactNode, Suspense, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import IconWalletArrow from "../../components/icons/IconWalletArrow";
import NavBar from "../../components/NavBar/NavBar";
import { RootState } from "../../store";
import { useStyles } from "./InvestorLayout.styles";
import { useSuppressHoverOnScroll } from "./useSuppressHoverOnScroll";

interface IProps {
  content: ReactNode;
}

const InvestorLayout: FC<IProps> = ({ content }) => {
  const isAuthRep = useSelector((state: RootState) => state.auth.isAuthRep);
  const { classes } = useStyles();
  const entityType = useSelector((state: RootState) => state.auth.entityType);
  const isMobile = useIsMobile();
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
  const location = useLocation();

  // Keyed on `location.key`, not `pathname`: tapping the nav entry for the
  // page you're already on is still a navigation (React Router records a new
  // history entry, so the key changes) but leaves the pathname untouched —
  // so a pathname-keyed effect never fired and the drawer stayed open. That
  // click is meaningful on `/offer-room`, where it also returns the screen
  // from the subscription agreement to the notes list (see StructureNote).
  useEffect(() => {
    closeDrawer();
  }, [location.key, closeDrawer]);

  useSuppressHoverOnScroll();

  // Corporate admins/directors without the AR role see only Investment
  // Portfolio, Transaction History and Onboarding Doc — see investorAccess.ts.
  // AuthRepOnlyRoute enforces the same split for typed-in URLs.
  const fullAccess = hasFullInvestorAccess(entityType, isAuthRep);

  const mainMenus = [
    ...(fullAccess
      ? [
          {
            label: "Offer Room",
            path: "/offer-room",
            icon: <IconFileDollar stroke={2} />,
          },
        ]
      : []),
    {
      label: "Investment Portfolio",
      path: "/investment-portfolio",
      icon: <IconChartBarPopular stroke={2} />,
    },
    {
      label: "Transaction History",
      path: "/transaction-history",
      icon: <IconHistory stroke={2} />,
    },
    ...(fullAccess
      ? [
          {
            label: "Withdrawal",
            path: "/withdrawal",
            icon: <IconWalletArrow />,
          },
          {
            label: "Risk Disclosure",
            path: "/risk-disclosure",
            icon: <IconShieldCheck stroke={2} />,
          },
        ]
      : []),
    {
      // Spelled out in the mobile drawer, abbreviated in the desktop
      // sidebar — the full label wraps to two lines in the sidebar's
      // narrower 240px column, making this the only nav entry that isn't a
      // single line. This is copy, not layout, so it's a legitimate use of
      // the `isMobile` branch this file already renders on (see the header
      // comment) rather than something CSS could express.
      label: isMobile ? "Onboarding Document" : "Onboarding Doc",
      path: "/investor-signed-documents",
      icon: <IconFileDescription stroke={2} />,
    },
  ];

  if (isMobile) {
    return (
      <div className={classes.mobileContainer}>
        <header className={classes.mobileTopBar}>
          <Burger
            opened={drawerOpened}
            onClick={toggleDrawer}
            color="#182230"
            size="sm"
            aria-label="Toggle navigation"
          />
          <img className={classes.mobileLogo} alt="FundedHere" src={Logo} />
        </header>
        <Drawer
          opened={drawerOpened}
          onClose={closeDrawer}
          position="left"
          size={280}
          padding={0}
          withCloseButton={false}
          classNames={{ content: classes.drawerContent, body: classes.drawerBody }}
        >
          <div className={classes.drawerHeader}>
            <ActionIcon onClick={closeDrawer} aria-label="Close navigation" variant="transparent">
              <IconX size={22} color="#182230" />
            </ActionIcon>
          </div>
          <NavBar menus={mainMenus} embedded />
        </Drawer>
        <main className={classes.mobileHero}>
          <Suspense fallback={<ContentLoader />}>{content}</Suspense>
        </main>
      </div>
    );
  }

  return (
    <div className={classes.mainContainer}>
      <NavBar menus={mainMenus} />
      <div className={classes.hero}>
        <Suspense fallback={<ContentLoader />}>{content}</Suspense>
      </div>
    </div>
  );
};

export default InvestorLayout;
